import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ColorPickerComponent } from '../../../../../common/components/color.picker/color.picker.component';
import { ExpandIndicatorComponent } from '../../../../../common/components/expand.indicator/expand.indicator.component';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { SharedModule } from '../../../../shared/shared.module';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  CreateItineraryCommand,
  Itinerary,
  ItineraryAttraction,
} from '../../../models/itinerary';
import { ConfirmDialogComponent } from '../../../../../common/components/confirm.dialog/confirm.dialog.component';
import { CommonModule } from '@angular/common';
import { ButtonTriggerEvent } from '../../../../../common/components/button/button.component';
import { catchError, of, takeUntil, tap } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { ImageLinkFileSelectorComponent } from '../../../../../common/components/image.link.file.selector/image.link.file.selector.component';
import { BaseComponent } from '../../../../../common/components/base.component';
import { ActivatedRoute } from '@angular/router';
import { ErrorAlertService } from '../../../../../common/services/error.alert/error.alert.service';
import { AttractionDescriptionGeneratorComponent } from '../attraction.description.generator/attraction.description.generator.component';
import { removeHtmlTags } from '../../../../../common/utils/string.extensions';
import { ModalService } from '../../../../../common/services/modal/modal.service';
import { AttractionDescriptionPreviewModalComponent } from '../attraction.description.preview.modal/attraction.description.preview.modal.component';

@Component({
  selector: 'ig-attraction-editor',
  standalone: true,
  imports: [
    ColorPickerComponent,
    ExpandIndicatorComponent,
    ConfirmDialogComponent,
    MatMenu,
    MatTooltip,
    SharedModule,
    CommonModule,
    AttractionDescriptionGeneratorComponent,
  ],
  templateUrl: './attraction.editor.component.html',
  styleUrl: './attraction.editor.component.scss',
})
export class AttractionEditorComponent extends BaseComponent implements OnInit {
  @Input() itinerary: Itinerary = null;
  @Input() attraction: ItineraryAttraction = null;
  @Input() triggerText = '';
  @Input() isJustAddedPlaces = false;

  @Output() descriptionChange = new EventEmitter<string>();
  @Output() preDescriptionChange = new EventEmitter<string>();
  @Output() postDescriptionChange = new EventEmitter<string>();
  @Output() colorSlider = new EventEmitter<string>();
  @Output() onRemove = new EventEmitter<ItineraryAttraction>();
  @Output() onMapIconSelected = new EventEmitter();
  @Output() descriptionGenerated: EventEmitter<any> = new EventEmitter();

  @ViewChild('imgUploader', { static: false })
  imgUploader: ImageLinkFileSelectorComponent;

  form: FormGroup;
  isShowConfirm = false;
  defaultMapIconUrl = `https://iti-images.s3.amazonaws.com/imgs/map-icon.png`;
  mapIcons = [
    `https://iti-images.s3.amazonaws.com/imgs/map-icon-bridge.png`,
    `https://iti-images.s3.amazonaws.com/imgs/map-icon-building.png`,
    `https://iti-images.s3.amazonaws.com/imgs/map-icon-building2.png`,
    `https://iti-images.s3.amazonaws.com/imgs/map-icon-wheel.png`,
    `https://iti-images.s3.amazonaws.com/imgs/map-icon-church.png`,
    `https://iti-images.s3.amazonaws.com/imgs/map-icon-mountain.png`,
    `https://iti-images.s3.amazonaws.com/imgs/map-icon-tree.png`,
    `https://iti-images.s3.amazonaws.com/imgs/map-icon-house.png`,
    `https://iti-images.s3.amazonaws.com/imgs/map-icon-tower.png`,
  ];

  constructor(
    public activatedRoute: ActivatedRoute,
    public modalService: ModalService,
    private _fb: FormBuilder,
    private _errorAlertService: ErrorAlertService
  ) {
    super({ activatedRoute });
    this.form = this._fb.group({
      description: [null, []],
      preDescription: [null, []],
      postDescription: [null, []],
    });
  }

  handleOpen(event, menuTrigger: MatMenuTrigger) {
    console.log('this.isJustAddedPlaces', this.isJustAddedPlaces);
    if (this.isJustAddedPlaces) {
      event.stopPropagation();
      menuTrigger.closeMenu();
      return;
    }

    this.onOpen();
  }

  onDescriptionChange($event) {
    const value = this.form.get('description').value;
    this.descriptionChange.emit(value);
  }

  onPreDescriptionChange($event) {
    const value = this.form.get('preDescription').value;
    this.preDescriptionChange.emit(value);
  }

  onPostDescriptionChange($event) {
    const value = this.form.get('postDescription').value;
    this.postDescriptionChange.emit(value);
    console.log('onPostDescriptionChange');
  }

  onColorSlider($event) {
    this.colorSlider.emit($event);
  }

  ngOnInit(): void {
    this.onOpen();
  }

  onOpen() {
    this.itinerary.attractions.forEach((attraction) => {
      if (attraction.item.description) {
        attraction.item.description = removeHtmlTags(
          attraction.item.description
        );
      }
    });

    this.setValue(this.attraction);
  }

  setValue(attraction: ItineraryAttraction) {
    this.form.get('description').setValue(attraction.item.description);
    this.form.get('preDescription').setValue(attraction.preDescription);
    this.form.get('postDescription').setValue(attraction.postDescription);
  }

  generated(
    data: { itinerary: Itinerary; selectedItems: any },
    trigger: MatMenuTrigger
  ) {
    const attraction = data.itinerary.attractions.find(
      (x) => x._id === this.attraction._id
    );
    if (attraction) {
      this.setValue(attraction);
    }
    // trigger?.closeMenu();
    this.modalService.show({
      component: AttractionDescriptionPreviewModalComponent,
      panelClass: 'ig-modal-w-80p',
      data: {
        ...data,
        hubName: this.hubName,
        parentTrigger: trigger,
      },
    });

    this.descriptionGenerated.emit();
  }

  remove() {
    this.isShowConfirm = true;
  }

  hide() {
    this.isShowConfirm = false;
  }

  selectIcon(icon: string, trigger: MatMenuTrigger) {
    this.attraction.mapIcon = icon;
    this.isShowConfirm = false;
    this.onMapIconSelected.emit();
    trigger?.closeMenu();
  }

  submit(event: ButtonTriggerEvent, trigger: MatMenuTrigger) {
    this.imgUploader
      .uploadImg()
      .pipe(
        takeUntil(this.isDestroyed),
        finalize(event.complete),
        tap((data) => {
          if (!data.url) return;

          this.attraction.mapIcon = data.url;
          event.complete();
          trigger?.closeMenu();
          this.onMapIconSelected.emit();
          this.imgUploader.clear();
        }),
        catchError((err) => {
          this._errorAlertService.showErrorAlert(err.message);
          return of(null);
        })
      )
      .subscribe();
  }
}
